export type StartTodayMembership = {
    _id: string;
    memberId: string;
    coachId: string;
    name: string;
    status: string;
    activationDate: string;
    expirationDate: string;
    initialPaymentDate?: string | null;
    isDeleted?: boolean;
    isCanceled?: boolean;
    source?: string;
    share?: unknown;
    isSharedMembership?: boolean;
    membershipSharePoolId?: string | null;
    sharedMembershipRole?: string | null;
    sharePolicySnapshot?: unknown;
    transferredFromMembershipId?: string | null;
    transferredToMembershipId?: string | null;
    transferRootMembershipId?: string | null;
};

const KST_OFFSET = 9 * 60 * 60 * 1000;

const dateMs = (value: string | Date): number => {
    if (!value) return NaN;
    // 시간대 없는 날짜·시각도 기기의 시간대와 무관하게 해석한다.
    const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value)
        ? `${value.replace(' ', 'T')}+09:00`
        : value;
    return new Date(normalized).getTime();
};

export const membershipKoreanDate = (value: string | Date): string | null => {
    const ms = dateMs(value);
    return Number.isFinite(ms) ? new Date(ms + KST_OFFSET).toISOString().slice(0, 10) : null;
};

export const membershipMonthDay = (value: string | Date): string | null => {
    const date = membershipKoreanDate(value);
    return date ? `${Number(date.slice(5, 7))}월 ${Number(date.slice(8, 10))}일` : null;
};

/** now는 호출자가 준다. 잔여 회차를 추정하거나 입력 목록을 정렬·수정하지 않는다. */
export const findStartTodayMembership = <T extends StartTodayMembership>(
    memberships: readonly T[],
    memberId: string,
    coachId: string,
    now: string | Date,
): T | null => {
    const today = membershipKoreanDate(now);
    if (!today || !memberId || !coachId) return null;
    const sameRelation = memberships.filter((membership) =>
        membership.memberId === memberId && membership.coachId === coachId &&
        membership.source !== 'group' && !membership.isDeleted && !membership.isCanceled,
    );
    const active = sameRelation.filter((membership) => membership.status === 'active');
    // 시작일이 없거나 이미 시작한 활성권이 있으면 다음 권을 권하지 않는다.
    if (active.some((membership) => {
        const start = membershipKoreanDate(membership.activationDate);
        return !start || start <= today;
    })) return null;

    const next = active.reduce<T | null>((earliest, membership) => {
        if (!earliest) return membership;
        const difference = dateMs(membership.activationDate) - dateMs(earliest.activationDate);
        return difference < 0 || (difference === 0 && membership._id < earliest._id) ? membership : earliest;
    }, null);
    if (!next) return null;

    // 가장 이른 다음 권이 부적격이면 뒤의 권으로 건너뛰지 않는다.
    if (!next._id || !next.initialPaymentDate || !membershipKoreanDate(next.expirationDate) ||
        next.share || next.isSharedMembership || next.membershipSharePoolId ||
        next.sharedMembershipRole || next.sharePolicySnapshot ||
        next.transferredFromMembershipId || next.transferredToMembershipId || next.transferRootMembershipId) return null;

    return sameRelation.some((membership) =>
        membership.status === 'expired' && dateMs(membership.activationDate) < dateMs(next.activationDate),
    ) ? next : null;
};
